import { CheckMailingInstructions } from '@/services/api/generated/policeDepartmentsService';

import {
  DataDisplay,
  DataGrid,
  ExternalLink,
  InfoCard,
  Metric,
  SectionHeader,
} from './ChannelDisplayComponents';
import { formatFeeAmount, MAIL_REQUIREMENT_LABELS } from './formatters';

interface MailingDetailsProps {
  mailingInstructions: CheckMailingInstructions;
}

export const MailingDetails = ({
  mailingInstructions,
}: MailingDetailsProps) => {
  const {
    instructions,
    address,
    payable_to,
    memo_instructions,
    wait_before_requesting_days,
    estimated_processing_days,
    required_items,
    special_requirements,
    fee_structure,
    citations,
  } = mailingInstructions;

  return (
    <div className="space-y-4">
      {/* Instructions */}
      {instructions && (
        <InfoCard>
          <SectionHeader>Mailing Instructions</SectionHeader>
          <p className="text-sm leading-relaxed text-gray-700">
            {instructions}
          </p>
        </InfoCard>
      )}

      {/* Address - DATA FIRST */}
      {address && (
        <InfoCard>
          <div className="space-y-1 text-base font-medium text-gray-900">
            {address.address_line1 && <div>{address.address_line1}</div>}
            {address.address_line2 && <div>{address.address_line2}</div>}
            <div>
              {[address.city, address.province_or_state, address.postal_or_zip]
                .filter(Boolean)
                .join(', ')}
            </div>
          </div>
          <div className="mt-1 text-xs uppercase tracking-wide text-gray-500">
            Mailing Address
          </div>
        </InfoCard>
      )}

      {/* Check Details - DATA FIRST */}
      {(payable_to || memo_instructions) && (
        <DataGrid>
          {payable_to && (
            <InfoCard>
              <DataDisplay value={payable_to} label="Make Check Payable To" />
            </InfoCard>
          )}
          {memo_instructions && (
            <InfoCard>
              <DataDisplay value={memo_instructions} label="Check Memo" />
            </InfoCard>
          )}
        </DataGrid>
      )}

      {/* Timing - METRICS STYLE */}
      {(wait_before_requesting_days || estimated_processing_days) && (
        <InfoCard>
          <DataGrid>
            {wait_before_requesting_days && (
              <Metric
                value={wait_before_requesting_days}
                suffix="days"
                label="Wait after incident"
              />
            )}
            {estimated_processing_days && (
              <Metric
                value={estimated_processing_days}
                suffix="days"
                label="Processing time"
              />
            )}
          </DataGrid>
        </InfoCard>
      )}

      {/* Fee Structure - LARGE FEE */}
      {fee_structure?.estimated_fee_cents && (
        <InfoCard>
          <div className="mb-3 flex items-baseline justify-between">
            <div className="text-3xl font-bold text-gray-900">
              {formatFeeAmount(fee_structure.estimated_fee_cents)}
            </div>
            <div className="text-xs uppercase tracking-wide text-gray-500">
              Estimated Fee
            </div>
          </div>

          {/* Fee Components */}
          {fee_structure.components && fee_structure.components.length > 0 && (
            <div className="space-y-2 border-t border-gray-200 pt-3">
              {fee_structure.components.map((component, idx) => (
                <div
                  key={idx}
                  className="flex justify-between gap-3 text-sm text-gray-600"
                >
                  <span className="flex-1">{component.description}</span>
                  <span className="font-semibold text-gray-900">
                    {formatFeeAmount(component.amount_cents)}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Fee Notes */}
          {fee_structure.notes && (
            <p className="mt-3 border-t border-gray-200 pt-3 text-xs italic text-gray-600">
              {fee_structure.notes}
            </p>
          )}

          {/* Confidence Score */}
          {fee_structure.confidence_score !== undefined &&
            fee_structure.confidence_score !== null &&
            fee_structure.confidence_score < 0.8 && (
              <div className="mt-3 border-t border-gray-200 pt-3">
                <div className="mb-2 flex items-center gap-3">
                  <span className="text-xs text-gray-600">Confidence:</span>
                  <div className="flex flex-1 items-center gap-2">
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-200">
                      <div
                        className="h-full bg-blue-600"
                        style={{
                          width: `${fee_structure.confidence_score * 100}%`,
                        }}
                      />
                    </div>
                    <span className="text-xs font-bold text-gray-900">
                      {Math.round(fee_structure.confidence_score * 100)}%
                    </span>
                  </div>
                </div>
                {fee_structure.reasoning && (
                  <p className="text-xs text-gray-600">
                    {fee_structure.reasoning}
                  </p>
                )}
              </div>
            )}
        </InfoCard>
      )}

      {/* Required Items */}
      {required_items && required_items.length > 0 && (
        <InfoCard>
          <SectionHeader>Required Items</SectionHeader>
          <ul className="space-y-2">
            {required_items.map((item) => (
              <li key={item} className="flex items-start gap-2 text-sm">
                <span className="mt-0.5 text-blue-600">•</span>
                <span className="text-gray-900">
                  {MAIL_REQUIREMENT_LABELS[item] || item}
                </span>
              </li>
            ))}
          </ul>
        </InfoCard>
      )}

      {/* Special Requirements - Warning Style */}
      {special_requirements && (
        <InfoCard variant="warning">
          <SectionHeader>Special Requirements</SectionHeader>
          <p className="text-sm font-medium text-amber-900">
            {special_requirements}
          </p>
        </InfoCard>
      )}

      {/* Citations */}
      {citations && citations.length > 0 && (
        <InfoCard>
          <SectionHeader>Sources</SectionHeader>
          <div className="space-y-2">
            {citations.map((citation, idx) => (
              <div key={idx}>
                <ExternalLink href={citation.url}>
                  {citation.title}
                </ExternalLink>
              </div>
            ))}
          </div>
        </InfoCard>
      )}
    </div>
  );
};
